import {
  AddClientModal,
  DeleteConfirmModal,
  FilterPopover,
  IconImage,
  RestrictUserModal,
  UnblockUserModal,
  UpdateClientModal,
} from "@/components";
import {
  black_filter_img,
  search_img,
  delete_img,
  unlock_right,
} from "@/constants";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import {
  useDeleteClientMutation,
  useGetAllClientsQuery,
  useGetAllSpOwnerPartnersQuery,
  useUpdateClientStatusMutation,
} from "@/services";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Skeleton,
} from "@mui/material";
import { FC, MouseEvent, ReactElement, useEffect, useState } from "react";
import { IoMdAdd, IoMdClose } from "react-icons/io";
import { useLocation } from "react-router-dom";
import {
  HeaderRightNode,
  StyledContainedButton,
  StyledTextInput,
} from "./styles";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import { tokens } from "@/tokens";
import { tableCompactSx } from "@/components/table/table-styles";
import { formatDateOrTime } from "@/utils";

const initialModalState = {
  type: "", // "delete", "restrict", "unblock"
  open: false,
  clientName: "",
  _id: "",
  isRestricted: false,
  email: "",
};

const initalFilterState: BasicFilters = {
  status: "",
  dateAdded: "",
  partner: "",
};

const defaultClientDetails: Client = {
  _id: "",
  firstName: "",
  email: "",
  createdAt: new Date(),
  isRestricted: false,
  profileImage: "",
};

const SPOWNERClients: FC = (): ReactElement => {
  const { updateQuery, getQueryValue } = useSearchQuery();
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [anchorElement, setAnchorElement] = useState<HTMLButtonElement | null>(
    null
  );
  const [filterState, setFilterState] = useState(initalFilterState);
  const [modalState, setModalState] = useState(initialModalState);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showUpdateClientModal, setShowUpdateClientModal] = useState(false);
  const [selectedPartner, setSelectedPartner] =
    useState<SPOwnerPartnerTypes | null>(null);

  const [clientDetails, setClientDetails] =
    useState<Client>(defaultClientDetails);
  const [shouldFetchPartners, setShouldFetchPartners] = useState(false);
  const [updateClientStatus, { isLoading: updateClientStatusLoading }] =
    useUpdateClientStatusMutation();
  const filterCount = Object.values(filterState).filter(Boolean).length;
  const location = useLocation();
  const hasPartner = new URLSearchParams(location.search).get("partner");

  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const user = useAppSelector((state) => state.auth.user);
  const isTDAdminSP =
    user?.userRole === roles.TDADMIN &&
    selectedTenant?.tenantType === "SERVICE_PROVIDER";
  const isSuperAdmin = user?.userRole === roles.TDADMIN;

  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllClientsQuery(
    {
      pageSize: limit,
      pageNo: page + 1,
      searchKeyword: getQueryValue("search"),
      ...(filterState.status && {
        isRestricted: filterState.status !== "active" ? true : false,
      }),
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
      startDate: filterState.dateAdded,
      assignedPartner: filterState.partner || hasPartner || "",
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: partnerData } = useGetAllSpOwnerPartnersQuery(
    {
      searchKeyword: "",
      ...(isTDAdminSP && {
        tenantId: selectedTenant?._id,
      }),
    },
    {
      skip: !shouldFetchPartners && !isTDAdminSP,
      refetchOnMountOrArgChange: true,
    }
  );

  useEffect(() => {
    if (hasPartner) {
      setShouldFetchPartners(true);
    }
  }, []);

  useEffect(() => {
    if (hasPartner) {
      const found = partnerData?.data?.find((ele) => ele?._id === hasPartner);
      const body = {
        label: found?.firstName + " " + found?.lastName,
        value: found?._id,
      };
      initalFilterState.partner = body.value || "";
      setFilterState((prev) => ({ ...prev, partner: body.value || "" }));
    } else {
      setFilterState((prev) => ({ ...prev, partner: "" }));
      initalFilterState.partner = "";
    }
  }, [partnerData, hasPartner]);

  const { data = [], meta } = apiData || {};
  const { show } = useSnackbar();
  const totalCount = meta?.countDocs || 0;
  const { open, clientName } = modalState;

  const [deleteClient, { isLoading: deleteClientLoading }] =
    useDeleteClientMutation();

  const debouncedSearch = useDebounce((val: string) => {
    updateQuery({ search: val });
  }, 500);

  const handleRowClick = (client: Client) => {
    setClientDetails(client);
    setShowUpdateClientModal(true);
  };

  const handleCloseModal = () => {
    setModalState(initialModalState);
  };

  const handleTriggerModal = (item: Client, type: string) => {
    setModalState({
      type,
      open: true,
      clientName: item.firstName,
      _id: item._id,
      isRestricted: item.isRestricted,
      email: item.email,
    });
  };

  const handleOpenFilterPopover = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorElement(event.currentTarget);
  };

  const handleOpenAddClientModal = () => {
    setShowAddClientModal(true);
    setClientDetails(defaultClientDetails);
    setSelectedPartner(null);
  };

  const handleFilterChange = async (e: BasicFilters) => {
    setFilterState(e);
  };

  const handleDeleteClient = async () => {
    const userId = {
      userId: modalState?._id as string,
      ...(isTDAdminSP && { tenantId: selectedTenant?._id }),
    };
    try {
      await deleteClient(userId).unwrap();
      handleCloseModal();
    } catch (error) {
      if (error instanceof Error) {
        show(error.message, "", "error");
      }
    }
  };

  const handleUpdateClientStatus = async () => {
    try {
      await updateClientStatus({
        email: modalState.email,
        isRestricted: !modalState.isRestricted,
      }).unwrap();
      show(
        `Client ${modalState.isRestricted ? "unblocked" : "restricted"}!`,
        "",
        "success"
      );
      setModalState(initialModalState);
    } catch (error: any) {
      show(error.message, "", "error");
    }
  };

  const renderSkeletonRows = () =>
    Array.from({ length: 5 }).map((_, index) => (
      <TableRow key={`skeleton-${index}`}>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        <TableCell><Skeleton variant="text" /></TableCell>
        {isSuperAdmin && <TableCell><Skeleton variant="text" /></TableCell>}
        <TableCell><Skeleton variant="text" width={150} /></TableCell>
      </TableRow>
    ));

  return (
    <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 2, pt: 3, pb: 1.5, flexShrink: 0 }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Typography fontWeight={tokens.fontWeight.semibold} variant="h5" flex={1} color={tokens.text.primary}>
            Clients
            {totalCount ? (
              <Box component="span" sx={{ color: tokens.text.muted, pl: 0.5 }}>
                ({totalCount})
              </Box>
            ) : null}
          </Typography>
          <HeaderRightNode gap={2}>
            <StyledTextInput
              type="text"
              placeholder="Search"
              handleChangeValue={debouncedSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <IconImage src={search_img} size="auto" />
                  </InputAdornment>
                ),
              }}
            />
            <Box sx={{ position: "relative" }}>
              {filterCount > 0 && (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: "grid",
                    placeContent: "center",
                    backgroundColor: tokens.status.danger,
                    color: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    top: "-10px",
                    right: "-9px",
                    zIndex: "1",
                  }}
                >
                  <Typography variant="caption" fontWeight={tokens.fontWeight.semibold} color="white">
                    {filterCount}
                  </Typography>
                </Box>
              )}
              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                style={{ borderColor: tokens.border.default, height: "34px" }}
                startIcon={<IconImage src={black_filter_img} size="auto" />}
                onClick={handleOpenFilterPopover}
              />
            </Box>
            <StyledContainedButton
              startIcon={<IoMdAdd />}
              size="large"
              title="Add Client"
              onClick={handleOpenAddClientModal}
            />
          </HeaderRightNode>
        </Stack>
      </Box>

      {/* Table */}
      <Box sx={{ flex: 1, overflow: 'hidden', px: 2, pb: 2 }}>
        <Box sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: tokens.radius.lg,
          border: `1px solid ${tokens.border.default}`,
          bgcolor: tokens.bg.surface,
          ...tableCompactSx,
        }}>
          <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Client Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Date Added</TableCell>
                  {isSuperAdmin && <TableCell>Client ID</TableCell>}
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading || isFetching ? renderSkeletonRows() : (
                  <>
                    {data.map((item) => (
                      <TableRow
                        key={item._id}
                        onClick={() => handleRowClick(item)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: tokens.bg.hover },
                          '& > td': { borderBottom: `1px solid ${tokens.border.light}` },
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {item.firstName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {item.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                            {formatDateOrTime(item.createdAt, "date")}
                          </Typography>
                          <Typography variant="caption" sx={{ color: tokens.text.muted, fontSize: tokens.fontSize.xs }}>
                            {formatDateOrTime(item.createdAt, "time")}
                          </Typography>
                        </TableCell>
                        {isSuperAdmin && (
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: tokens.text.primary, fontSize: tokens.fontSize.base }}>
                              {item._id}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            {!item.isRestricted ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IoMdClose />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleTriggerModal(item, "restrict");
                                }}
                                sx={{
                                  color: "rgba(234, 67, 52, 1)",
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Restrict
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<IconImage src={unlock_right} size="auto" />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleTriggerModal(item, "unblock");
                                }}
                                sx={{
                                  color: "rgba(52, 168, 83, 1)",
                                  borderColor: tokens.border.default,
                                  fontWeight: tokens.fontWeight.semibold,
                                  fontSize: tokens.fontSize.xs,
                                  textTransform: "none",
                                }}
                              >
                                Unblock
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleTriggerModal(item, "delete");
                              }}
                              sx={{
                                color: tokens.status.danger,
                                borderColor: tokens.border.default,
                                minWidth: 'auto',
                                px: 1,
                              }}
                            >
                              <IconImage src={delete_img} size="auto" />
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {data.length === 0 && !isLoading && (
                      <TableRow>
                        <TableCell colSpan={isSuperAdmin ? 5 : 4} align="center" sx={{ py: 6 }}>
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            No clients found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {totalCount > 0 && (
            <Box sx={{ borderTop: `1px solid ${tokens.border.light}`, flexShrink: 0 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={limit}
                onRowsPerPageChange={(e) => {
                  setLimit(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                sx={{
                  '.MuiTablePagination-toolbar': { minHeight: 40 },
                  '.MuiTablePagination-displayedRows': { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                  '.MuiTablePagination-selectLabel': { fontSize: tokens.fontSize.base, color: tokens.text.secondary },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <FilterPopover
        anchorEl={anchorElement}
        onFiltersChange={handleFilterChange}
        initialFilterState={initalFilterState}
        onClose={() => setAnchorElement(null)}
        partnerData={partnerData}
      />
      {/* Delete confirmation Modal */}
      {modalState.type === "delete" && (
        <DeleteConfirmModal
          open={open}
          itemName={`Client "${clientName}"`}
          subText="client"
          onConfirm={handleDeleteClient}
          maxWidth="xs"
          onClose={handleCloseModal}
          btnLoader={deleteClientLoading}
        />
      )}
      {/* Restrict User Modal */}
      {modalState.type === "restrict" && (
        <RestrictUserModal
          description="client"
          open={open}
          name={clientName}
          onConfirm={handleUpdateClientStatus}
          onClose={handleCloseModal}
          loading={updateClientStatusLoading}
        />
      )}
      {/* Unblock User Modal */}
      {modalState.type === "unblock" && (
        <UnblockUserModal
          description="client"
          open={open}
          name={clientName}
          onConfirm={handleUpdateClientStatus}
          onClose={handleCloseModal}
          loading={updateClientStatusLoading}
        />
      )}
      {/* Add Client Modal */}
      {showAddClientModal && (
        <AddClientModal
          show={showAddClientModal}
          setShow={setShowAddClientModal}
          selectedPartner={selectedPartner}
          setSelectedPartner={setSelectedPartner}
        />
      )}
      {/* Update Client Modal */}
      {showUpdateClientModal && (
        <UpdateClientModal
          show={showUpdateClientModal}
          setShow={setShowUpdateClientModal}
          selectedPartner={selectedPartner}
          setSelectedPartner={setSelectedPartner}
          clientDetails={clientDetails}
          setClientDetails={setClientDetails}
        />
      )}
    </Stack>
  );
};

export default SPOWNERClients;
